import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MessageCircle, Building2, User, Trash2, Edit, Calendar } from 'lucide-react';
import ModernSettingHeader from '@/components/settings/ModernSettingHeader';
import { AuthService } from '@/services/authService';

interface Registration {
  registration_id: number;
  uuid: string;
  full_name: string;
  email: string;
  phone_number: string;
  company_name: string;
  company_description?: string;
  country: string;
  state?: string;
  company_size?: string;
  industry: string;
  website_url?: string;
  timezone: string;
  status: string;
  created_at: string;
}

const Registration: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    company_name: '',
    company_description: '',
    country: 'India', // Default to India
    state: '',
    company_size: '',
    industry: '',
    website_url: '',
    timezone: 'Asia/Kolkata' // Default to IST
  });

  const indianStates = [
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Andaman and Nicobar Islands',
    'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi',
    'Jammu and Kashmir',
    'Ladakh',
    'Lakshadweep',
    'Puducherry'
  ];

  useEffect(() => {
    loadRegistrations();
  }, []);

  const loadRegistrations = async () => {
    try {
      setLoadingRegistrations(true);
      const token = AuthService.getToken();
      const response = await fetch(`${import.meta.env.VITE_API_URL}/whatsapp-automation/registrations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success && data.data) {
        setRegistrations(data.data.data || []);
      }
    } catch (error) {
      console.error('Error loading registrations:', error);
    } finally {
      setLoadingRegistrations(false);
    }
  };

  const handleEdit = (registration: Registration) => {
    setIsEditing(true);
    setEditingUuid(registration.uuid);
    setFormData({
      full_name: registration.full_name,
      email: registration.email,
      phone_number: registration.phone_number,
      company_name: registration.company_name,
      company_description: registration.company_description || '',
      country: registration.country,
      state: registration.state || '',
      company_size: registration.company_size || '',
      industry: registration.industry,
      website_url: registration.website_url || '',
      timezone: registration.timezone
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingUuid(null);
    setFormData({
      full_name: '',
      email: '',
      phone_number: '',
      company_name: '',
      company_description: '',
      country: 'India',
      state: '',
      company_size: '',
      industry: '',
      website_url: '',
      timezone: 'Asia/Kolkata'
    });
  };

  const handleDelete = async (uuid: string) => {
    if (!confirm('Are you sure you want to delete this registration?')) {
      return;
    }

    try {
      const token = AuthService.getToken();
      const response = await fetch(`${import.meta.env.VITE_API_URL}/whatsapp-automation/registrations/${uuid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Registration deleted successfully');
        loadRegistrations();
      } else {
        toast.error(data.error || 'Failed to delete registration');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete registration');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);

      const token = AuthService.getToken();
      const url = isEditing
        ? `${import.meta.env.VITE_API_URL}/whatsapp-automation/registrations/${editingUuid}`
        : `${import.meta.env.VITE_API_URL}/whatsapp-automation/register`;

      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          full_name: formData.full_name,
          email: formData.email,
          phone_number: formData.phone_number,
          company_name: formData.company_name,
          company_description: formData.company_description,
          country: formData.country,
          state: formData.state,
          company_size: formData.company_size,
          industry: formData.industry,
          website_url: formData.website_url,
          timezone: formData.timezone
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(data.message || (isEditing ? 'Registration updated successfully!' : 'Registration successful!'));
        setSubmitted(true);
        setIsEditing(false);
        setEditingUuid(null);
        loadRegistrations(); // Reload registrations after successful submission
      } else {
        toast.error(data.error || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Failed to submit registration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if user has existing registrations
  if (loadingRegistrations) {
    return (
      <div className="w-full">
        <ModernSettingHeader
          title="WhatsApp Automation Registration"
          description="Register for WhatsApp automation services"
          icon={<MessageCircle className="h-5 w-5" />}
        />
        <div className="p-6">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading registrations...</p>
          </div>
        </div>
      </div>
    );
  }

  // If user already has a registration and not editing, show it
  if (registrations.length > 0 && !isEditing) {
    const registration = registrations[0]; // Show the first/latest registration

    return (
      <div className="w-full">
        <ModernSettingHeader
          title="WhatsApp Automation Registration"
          description="Your WhatsApp automation service registration"
          icon={<MessageCircle className="h-5 w-5" />}
        />

        <div className="p-6">
          <div className="max-w-4xl mx-auto">
            {submitted && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
                <p className="text-green-800 dark:text-green-200 text-center">
                  ✓ Registration successful! Our team will be contacting you shortly.
                </p>
              </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              {/* Status Badge */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Registration Details</h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  registration.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                  registration.status === 'in_progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                  registration.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                  'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                  {registration.status.charAt(0).toUpperCase() + registration.status.slice(1)}
                </span>
              </div>

              {/* Contact Information */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Full Name</label>
                    <p className="text-gray-900 dark:text-white font-medium">{registration.full_name}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Email</label>
                    <p className="text-gray-900 dark:text-white font-medium">{registration.email}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Phone Number</label>
                    <p className="text-gray-900 dark:text-white font-medium">{registration.phone_number}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Timezone</label>
                    <p className="text-gray-900 dark:text-white font-medium">{registration.timezone}</p>
                  </div>
                </div>
              </div>

              {/* Company Information */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">Company Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Company Name</label>
                    <p className="text-gray-900 dark:text-white font-medium">{registration.company_name}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Industry</label>
                    <p className="text-gray-900 dark:text-white font-medium">{registration.industry}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Country</label>
                    <p className="text-gray-900 dark:text-white font-medium">{registration.country}</p>
                  </div>
                  {registration.state && (
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">State</label>
                      <p className="text-gray-900 dark:text-white font-medium">{registration.state}</p>
                    </div>
                  )}
                  {registration.company_size && (
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Company Size</label>
                      <p className="text-gray-900 dark:text-white font-medium">{registration.company_size}</p>
                    </div>
                  )}
                  {registration.website_url && (
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Website</label>
                      <p className="text-gray-900 dark:text-white font-medium">
                        <a href={registration.website_url} target="_blank" rel="noopener noreferrer" className="text-brandColor hover:underline">
                          {registration.website_url}
                        </a>
                      </p>
                    </div>
                  )}
                </div>
                {registration.company_description && (
                  <div className="mt-4">
                    <label className="text-xs text-gray-500 dark:text-gray-400">Description</label>
                    <p className="text-gray-900 dark:text-white">{registration.company_description}</p>
                  </div>
                )}
              </div>

              {/* Registration Date */}
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Calendar className="h-4 w-4" />
                  <span>Registered on {new Date(registration.created_at).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => handleEdit(registration)}
                  className="flex items-center gap-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover transition-colors"
                >
                  <Edit className="h-4 w-4" />
                  Update Registration
                </button>
                <button
                  onClick={() => handleDelete(registration.uuid)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Registration
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ModernSettingHeader
        title={isEditing ? "Update WhatsApp Automation Registration" : "WhatsApp Automation Registration"}
        description={isEditing ? "Update your WhatsApp automation service registration" : "Register for WhatsApp automation services powered by AiSensy and other providers"}
        icon={<MessageCircle className="h-5 w-5" />}
      />

      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Information */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <User className="h-5 w-5 text-brandColor" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Building2 className="h-5 w-5 text-brandColor" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Company Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Industry <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Education, E-commerce, Healthcare"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Company Description
                  </label>
                  <textarea
                    name="company_description"
                    value={formData.company_description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white bg-gray-50 dark:bg-gray-800"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select State</option>
                    {indianStates.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Company Size
                  </label>
                  <select
                    name="company_size"
                    value={formData.company_size}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501+">501+ employees</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Website URL
                  </label>
                  <input
                    type="url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    placeholder="https://example.com"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Timezone
                  </label>
                  <select
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                  >
                    <option value="Asia/Kolkata">IST (Asia/Kolkata)</option>
                    <option value="America/New_York">EST (America/New_York)</option>
                    <option value="America/Los_Angeles">PST (America/Los_Angeles)</option>
                    <option value="Europe/London">GMT (Europe/London)</option>
                    <option value="Asia/Dubai">GST (Asia/Dubai)</option>
                    <option value="Asia/Singapore">SGT (Asia/Singapore)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              {isEditing && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    {isEditing ? 'Updating...' : 'Submitting...'}
                  </>
                ) : (
                  <>
                    {isEditing ? <Edit className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
                    {isEditing ? 'Update Registration' : 'Submit Registration'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Registration;
